import React from "react"
import { Avatar, Box, ButtonBase, Grid2, List, ListSubheader } from "@mui/material"
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material"
import { User as UserIcon } from "lucide-react"

import { Text } from "@/components/Text"

import { NavPopper } from "@/components/Sidebar/NavPopper"
import { NavItem } from "@/components/Sidebar/NavItem"
import { MenuItem } from "@/components/Sidebar/protocols"
import { colors } from "@/styles/colors"
import useStyles from "@/components/Sidebar/ProfileMenu/styles"

type ProfileMenuProps = {
	username: string
	email?: string
	avatarUrl?: string
	onClick: () => void
	variant?: "inbox" | "console"
	isCollapsed?: boolean
	accountItems?: MenuItem[]
	accountLabel?: string
}

export const ProfileMenu = ({ username, email, avatarUrl, onClick, variant = "inbox", isCollapsed = false, accountItems, accountLabel = "Minha conta" }: ProfileMenuProps) => {
	const classes = useStyles()

	const isConsole = variant === "console"
	const profileMenuSx = [classes.profileMenu, isConsole && classes.profileMenuSecondary, isCollapsed && classes.profileMenuCollapsed]
	const avatarSx = [classes.avatar, isConsole && classes.avatarSecondary]
	const nameTextSx = [classes.nameText, isConsole && classes.nameTextSecondary]
	const emailTextSx = [classes.emailText, isConsole && classes.emailTextSecondary]

	const trigger = (
		<Grid2 size={12}>
			<ButtonBase sx={profileMenuSx} onClick={onClick}>
				<Avatar src={avatarUrl} sx={avatarSx}>
					{!avatarUrl && <UserIcon size={18} />}
				</Avatar>
				{!isCollapsed && (
					<>
						<Box sx={classes.usernameWrapper}>
							<Text.Body type="smaller1" sx={nameTextSx}>{username}</Text.Body>
							{email && (
								<Text.Body type="smaller2" sx={emailTextSx}>{email}</Text.Body>
							)}
						</Box>
						<ChevronRightIcon sx={[classes.chevron, isConsole && classes.chevronSecondary]} />
					</>
				)}
			</ButtonBase>
		</Grid2>
	)

	if (!accountItems) {
		return trigger
	}

	return (
		<NavPopper
			placement="right-end"
			panelContent={
				<List
					disablePadding
					sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
					subheader={
						<ListSubheader
							disableSticky
							disableGutters
							component="div"
							sx={{ color: colors.gray[500], fontSize: "0.8125rem", fontWeight: 500, padding: "0 16px 8px 16px" }}
						>
							{accountLabel}
						</ListSubheader>
					}
				>
					{accountItems.map((item) => (
						<NavItem
							key={item.label}
							icon={item.icon}
							label={item.label}
							isDisabled={item.isDisabled}
							isActive={item.isActive}
							tone={item.tone}
							featureType={item.featureType}
							href={item.href}
							variant={variant}
							onClick={item.onClick}
						/>
					))}
				</List>
			}
		>
			{trigger}
		</NavPopper>
	)
}
